import {React, observer, computed, useLocalStore, useAsObservableSource, Fragment, action, autorun, observable, useEffect, classNames} from '@api'
import {Card, Elevation} from '@blueprintjs/core'
import {FrameShape, TldrawApp, useTldrawApp, TDShape, getFontFace, getFontSize, getTextAlign} from '@modules/whiteboard'
import {HTMLContainer, TLComponentProps, Utils} from '@tldraw/core'
import {FrameUtil} from '.'
import {ShapeComponentStore} from '@modules/whiteboard/shapes/ShapeComponentStore'
import * as css from './FrameShapeComponent.css'

/// region Components

export class FrameShapeStore extends ShapeComponentStore<FrameShape, FrameUtil> {
  constructor(public props: TLComponentProps<FrameShape> & { app: TldrawApp }) {
    super(props)
  }

  get shape() { return this.props.shape}

  @computed get rootCssStyle() {
    const {shape: {size: [width, height]}} = this
    return {
      width: `${width}px`,
      height: `${height}px`
    } as any
  }

  @computed get cssStyle() {
    const {shape: {objectFit, style, size: [width, height]}} = this
    return {
      '--object-fit': objectFit ?? 'contain',
      '--background-color': style?.backgroundColor
    } as any
  }

  @action onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.shape.title = e.currentTarget.value
  }

  registerMyEffects = () => {
    const r = this.registerEffects()

    this._toDispose.push(
      autorun(() => {
        const {app: {isInSession}} = this
        if (!isInSession) {
          this.shapesContainedBy = [...this._shapesContainedBy]
        }
      })
    )
    return r
  }

  @observable.ref shapesContainedBy: Array<TDShape> = []

  @computed get shapesContainedByInOrder() {
    return this.shapesContainedBy.sort(
      ({point: [ax, ay]}, {point: [bx, by]}) =>
        ax === bx && ay === by
        ? 0
        : ay < by
          ? -1
          : ay === by && ax < bx
            ? -1
            : 1)
  }

  @action onSelectContentShapes = () => {
    const {shapesContainedBy, app: {myPageState}} = this
    myPageState.selectedIds = shapesContainedBy.map(s => s.id)
  }

  @computed
  private get _shapesContainedBy() {
    const {app: {shapes, shapeUtilFor, shapeUtil: {getBounds}}, bounds, shape} = this
    return shapes.filter(s => s != shape && Utils.boundsCollide(bounds, getBounds(s)))
  }

  @computed get shapesContainedBySet() { return new Set<string>(this.shapesContainedBy.map(s => s.id))}

  @computed get textDecoration() {
    const {shape: {style: {fontStyle}}} = this
    return classNames(fontStyle?.underline && 'underline', fontStyle?.strikethrough && 'line-through')
  }

  @computed get fontWeight() {
    const {shape: {style: {fontStyle}}} = this
    return fontStyle?.bold ? 'bold' : 'normal'
  }

  @computed get fontStyle() {
    const {shape: {style: {fontStyle}}} = this
    return fontStyle?.italic ? 'italic' : undefined
  }

  @computed get fontFace() {
    return getFontFace(this.shape.style.font)
  }

  @computed get titleCssVars(): any {
    const {shape: {style}, fontStyle, fontFace, shapeStyle: {textColor}, fontWeight, textDecoration} = this

    return {
      '--font-family': fontFace,
      '--text-color': textColor,
      '--font-size': getFontSize(style),
      '--text-align': getTextAlign(style.textAlign),
      '--font-weight': fontWeight,
      '--text-decoration': textDecoration,
      '--font-style': fontStyle
    }
  }
}

export const FrameShapeComponent = observer<TLComponentProps<FrameShape>, any>(function FrameShapeComponent(props, ref) {
  const app = useTldrawApp(),
    obsProps = useAsObservableSource({...props, app}),
    store = useLocalStore(() => new FrameShapeStore(obsProps)),
    {rootCssStyle, props: {shape, isSelected, events}, titleCssVars, onChangeTitle, cssStyle} = store,
    {title, isGenerated} = shape

  useEffect(() => store.registerMyEffects(), [])

  return (
    <HTMLContainer ref={ref} {...events}>
      <div className={css.root} data-creating={isGenerated} style={rootCssStyle}>
        <div className={css.title} style={titleCssVars}>
          <input value={title} onChange={onChangeTitle}/>
        </div>
        <Card elevation={Elevation.TWO} className={css.card} style={cssStyle} data-selected={isSelected}>
        </Card>
      </div>
    </HTMLContainer>
  )
}, {forwardRef: true})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/frame/FrameShapeComponent.tsx